import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
    InfiniteData,
    Query,
    UseInfiniteQueryOptions,
    keepPreviousData,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { last } from 'lodash';
import { EngageDNSRecord, EngageDomain } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const transformInfiniteQueryOptions = <T>(): Pick<
    UseInfiniteQueryOptions<
        PaginatedData<T>,
        DefaultError,
        InfiniteData<PaginatedData<T>['data'][0], number>,
        PaginatedData<T>,
        QueryKey,
        number
    >,
    'select' | 'getNextPageParam' | 'placeholderData' | 'refetchOnWindowFocus'
> => ({
    select: data => {
        return {
            ...data,
            pages: data.pages.flatMap(d => d.data),
        };
    },
    getNextPageParam: lastPage =>
        lastPage.meta.current_page < lastPage.meta.last_page
            ? lastPage.meta.current_page + 1
            : undefined,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
});

const transformEngageDomainQuery = () => {
    return {
        select: (response: AxiosResponse<PaginatedData<EngageDomain>>) => {
            const customDomain: EngageDomain | undefined = response.data.data.find(
                (rec: EngageDomain) => {
                    return !rec.is_subdomain;
                },
            );

            const givebutterDomain: EngageDomain | undefined = response.data.data.find(
                (rec: EngageDomain) => {
                    return rec.is_subdomain;
                },
            );

            const customDomainHasUnverifiedRecords =
                customDomain?.records.length &&
                customDomain.records.find((rec: EngageDNSRecord) => {
                    return rec.verified_at === null;
                });

            return {
                customDomain,
                givebutterDomain,
                customDomainHasUnverifiedRecords,
            };
        },
    };
};

const conditionallyFetchOrNull = <T>(fn: (...args: any) => T, cond: boolean = true) => {
    if (cond) {
        return fn();
    }

    return null;
};

const isQueryKeyIndexMeta = (query: Query) => {
    const possibleMeta = last(query.queryKey);
    return typeof possibleMeta === 'object' && !!possibleMeta?.hasOwnProperty('page');
};

export {
    transformInfiniteQueryOptions,
    transformEngageDomainQuery,
    conditionallyFetchOrNull,
    isQueryKeyIndexMeta,
};
